// ===========================================
// Class Link Dialog — add / edit a web link on a class (2026-09-07)
// ===========================================

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { ClassLink } from '../../api/classLinks.api';
import { FileVisibility, getVisibilityLabel } from '../../api/resources.api';
import { useCreateClassLink, useUpdateClassLink } from '../../hooks/useClassLinks';
import { isSafeHttpsUrl } from '../../utils/safeUrl';

const VISIBILITIES: FileVisibility[] = ['ALL', 'TEACHERS_AND_PARENTS', 'TEACHERS_ONLY'];

export interface ClassLinkDialogProps {
  open: boolean;
  onClose: () => void;
  /** The class the dialog was opened from. */
  lessonId: string;
  /** The class's program level, if any — enables "share with every class in the level". */
  programLevel?: { id: string; name: string } | null;
  /** Edit mode when set. */
  link?: ClassLink | null;
}

type ApiError = Error & { response?: { status?: number; data?: { message?: string } } };

export default function ClassLinkDialog({ open, onClose, lessonId, programLevel, link }: ClassLinkDialogProps) {
  const create = useCreateClassLink();
  const update = useUpdateClassLink();
  const isEdit = !!link;

  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<FileVisibility>('ALL');
  const [shareWithLevel, setShareWithLevel] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUrl(link?.url ?? '');
    setTitle(link?.title ?? '');
    setDescription(link?.description ?? '');
    setVisibility(link?.visibility ?? 'ALL');
    setShareWithLevel(!!link?.programLevelId);
    setServerError(null);
  }, [open, link]);

  const urlOk = isSafeHttpsUrl(url);
  const urlTouchedAndBad = url.trim().length > 0 && !urlOk;
  const canSave = urlOk && title.trim().length > 0 && !create.isPending && !update.isPending;

  // Suggest a title from the host the first time a valid URL is pasted (edit mode keeps its own).
  const suggestedTitle = useMemo(() => {
    if (!urlOk) return '';
    try {
      return new URL(url.trim()).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  }, [url, urlOk]);

  const handleSave = () => {
    setServerError(null);
    const onError = (error: ApiError) => setServerError(error.response?.data?.message ?? 'Could not save the link.');
    if (isEdit && link) {
      update.mutate(
        { id: link.id, data: { url: url.trim(), title: title.trim(), description: description.trim() || null, visibility } },
        { onSuccess: onClose, onError }
      );
      return;
    }
    const target = shareWithLevel && programLevel ? { programLevelId: programLevel.id } : { lessonId };
    create.mutate(
      { ...target, url: url.trim(), title: title.trim(), description: description.trim() || undefined, visibility },
      { onSuccess: onClose, onError }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit link' : 'Add a link'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Link (https://…)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => { if (!title && suggestedTitle) setTitle(suggestedTitle); }}
            fullWidth
            required
            autoFocus={!isEdit}
            error={urlTouchedAndBad}
            helperText={urlTouchedAndBad ? 'Only https:// links can be shared' : 'A YouTube video, a website, anything with an https:// address'}
            inputProps={{ inputMode: 'url' }}
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 200))}
            fullWidth
            required
            helperText="What families will see on the card"
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel id="class-link-visibility-label">Visibility</InputLabel>
            <Select
              labelId="class-link-visibility-label"
              label="Visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as FileVisibility)}
              inputProps={{ 'aria-label': 'Visibility' }}
            >
              {VISIBILITIES.map((v) => (
                <MenuItem key={v} value={v}>{getVisibilityLabel(v)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isEdit && programLevel && (
            <FormControlLabel
              control={<Switch checked={shareWithLevel} onChange={(e) => setShareWithLevel(e.target.checked)} />}
              label={`Share with every class in ${programLevel.name}`}
            />
          )}
          {serverError && <Alert severity="error">{serverError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={create.isPending || update.isPending}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {create.isPending || update.isPending ? <CircularProgress size={20} /> : isEdit ? 'Save' : 'Add link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
